import React, { Component, ErrorInfo, ReactNode } from 'react';
import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonIcon } from '@ionic/react';
import { alertCircleOutline, refreshOutline } from 'ionicons/icons';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component to catch JavaScript errors in child components
 * and display a fallback UI instead of crashing the entire app.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({ errorInfo });

    // You could also log to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  /* eslint-disable class-methods-use-this */
  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };
  /* eslint-enable class-methods-use-this */

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            padding: '20px',
            backgroundColor: '#f5f5f5',
          }}
        >
          <IonCard style={{ maxWidth: '500px', width: '100%' }}>
            <IonCardHeader>
              <IonIcon
                icon={alertCircleOutline}
                style={{ fontSize: '48px', color: 'var(--ion-color-danger)', marginBottom: '10px' }}
              />
              <IonCardTitle>Something went wrong</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                We're sorry, but something unexpected happened. Please try refreshing the page or return to the home
                page.
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details style={{ marginBottom: '20px', fontSize: '14px' }}>
                  <summary style={{ cursor: 'pointer', color: 'var(--ion-color-danger)' }}>Error Details</summary>
                  <pre
                    style={{
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '5px',
                      overflow: 'auto',
                      fontSize: '12px',
                    }}
                  >
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <IonButton onClick={this.handleReload} color="primary">
                  <IonIcon slot="start" icon={refreshOutline} />
                  Refresh Page
                </IonButton>
                <IonButton onClick={this.handleGoHome} fill="outline" color="primary">
                  Go to Home
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
